import CloseIcon from '@mui/icons-material/Close';
import { Button, Checkbox, Grid, Stack, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from 'src/components/basic/Sidebar';
import { useAppDispatch } from 'src/hooks/store';
import {
  deleteTemplateOrganization,
  selectTemplateOrganization,
  updateTemplateOrganization,
} from 'src/slices/organization';
import {
  Organization,
  OrganizationTemplate,
} from 'src/types/organization.type';

interface Props {
  open: boolean;
  onClose: () => void;
  organization: Organization | null;
}

export const OrganizationExportEdition: React.FC<Props> = ({
  open,
  onClose,
  organization,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const coverInputRef = useRef<HTMLInputElement>(null);
  const bodyInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const onTemplateUpload = async (file: File, type: 'cover' | 'body') => {
    if (!organization?._id) return;
    setIsUploading(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('filename', file.name);

    await dispatch(
      updateTemplateOrganization({
        organizationId: organization._id,
        data: { formData },
      }),
    );

    setIsUploading(false);
  };
  const onTemplateDelete = async (storageName: string) => {
    if (!organization?._id) return;
    await dispatch(
      deleteTemplateOrganization({
        organizationId: organization._id,
        storageName,
      }),
    );
  };

  const onTemplateSelect = async (
    organizationId: string,
    storageName: string,
    type: 'cover' | 'body',
  ) => {
    await dispatch(
      selectTemplateOrganization({
        organizationId,
        storageName,
        type,
      }),
    );
  };

  const getTemplatesByType = (type: 'cover' | 'body') =>
    organization?.templates?.filter((template) => template.type === type) || [];

  const handleSelectTemplate = (template: OrganizationTemplate) => {
    if (organization) {
      if (template.isSelected) {
        onTemplateSelect(organization._id, '', template.type);
      } else {
        onTemplateSelect(organization._id, template.storageName, template.type);
      }
    }
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'cover' | 'body',
  ) => {
    if (e.target.files && e.target.files[0]) {
      await onTemplateUpload(e.target.files[0], type);
    }
  };

  return (
    <Sidebar
      header={
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t('pages.OrganizationConfiguration.templates.exportSettings')}
        </Typography>
      }
      body={
        <Stack spacing={4} sx={{ pt: 5 }}>
          <Typography sx={{ maxWidth: 1000, whiteSpace: 'pre-line' }}>
            {t('pages.OrganizationConfiguration.templates.exportDescription')}
          </Typography>
          <Grid container spacing={4} sx={{ pr: 5 }}>
            {(['cover', 'body'] as const).map((type) => (
              <Grid item xs={12} md={6} key={type}>
                <Stack spacing={2}>
                  <Typography fontWeight="bold">
                    {type === 'cover'
                      ? t(
                          'pages.OrganizationConfiguration.templates.uploadCoverTitle',
                        )
                      : t(
                          'pages.OrganizationConfiguration.templates.uploadBodyTitle',
                        )}
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={isUploading}
                    sx={{ minWidth: 40, width: 40, height: 40, p: 0 }}
                  >
                    <span style={{ fontWeight: 'bold', fontSize: 18 }}>+</span>
                    <input
                      ref={type === 'cover' ? coverInputRef : bodyInputRef}
                      type="file"
                      accept=".docx"
                      hidden
                      onChange={(e) => handleFileChange(e, type)}
                    />
                  </Button>
                  <Stack spacing={1}>
                    {getTemplatesByType(type).length === 0 && (
                      <Typography fontStyle="italic">
                        {t(
                          'pages.OrganizationConfiguration.templates.uploadWordFile',
                        )}
                      </Typography>
                    )}
                    {getTemplatesByType(type).map((template) => (
                      <Stack
                        key={template.storageName}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          border: '1px solid #eee',
                          borderRadius: 1,
                          p: 1,
                          justifyContent: 'space-between',
                        }}
                      >
                        <Checkbox
                          checked={!!template.isSelected}
                          onChange={() => handleSelectTemplate(template)}
                          inputProps={{ 'aria-label': 'select template' }}
                        />
                        <span
                          style={{
                            color: template.isSelected ? '#1976d2' : 'inherit',
                            cursor: 'pointer',
                          }}
                        >
                          {template.filename}
                        </span>
                        <Button
                          size="small"
                          onClick={() => onTemplateDelete(template.storageName)}
                          sx={{ minWidth: 0, p: 0, ml: 1 }}
                        >
                          <CloseIcon fontSize="small" />
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Grid>
            ))}
          </Grid>
          {isUploading && (
            <Typography>
              {t('pages.OrganizationConfiguration.templates.uploading')}
            </Typography>
          )}
        </Stack>
      }
      footer={null}
      isOpen={open}
      closeSideBar={onClose}
      justifyStart={true}
    />
  );
};
